import { FileIcon } from "@untitledui/file-icons";
import { RefreshCw01, Trash02, UploadCloud02 } from "@untitledui/icons";
import type { ComponentPropsWithRef, DragEvent, ReactNode } from "react";
import { useCallback, useId, useRef, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { ProgressBarBase } from "@/components/base/progress-indicators/progress-indicators";
import { cx } from "@/utils/cx";

/**
 * Metadata for a file shown in the upload list (no `File` reference required).
 */
export interface UploadedFile {
	id: string;
	name: string;
	size: number;
	/** MIME type (e.g. `image/jpeg`) or extension (e.g. `pdf`) for {@link FileIcon}. */
	type: string;
	progress: number;
	failed?: boolean;
}

/**
 * Formats a byte size as a human-readable string (base-1024).
 *
 * @param bytes - Size in bytes.
 * @returns A short label such as `720 KB` or `1.5 MB`.
 */
export function getReadableFileSize(bytes: number): string {
	if (bytes === 0) return "0 B";
	const units = ["B", "KB", "MB", "GB", "TB"] as const;
	const i = Math.min(
		units.length - 1,
		Math.floor(Math.log(bytes) / Math.log(1024)),
	);
	const value = bytes / 1024 ** i;
	const decimals = i === 0 ? 0 : value < 10 ? 1 : 0;
	return `${value.toFixed(decimals)} ${units[i]}`;
}

const Root = ({ className, ...props }: ComponentPropsWithRef<"div">) => (
	<div {...props} className={cx("flex w-full flex-col gap-4", className)} />
);

interface DropZoneProps
	extends Omit<ComponentPropsWithRef<"div">, "children" | "title"> {
	/** Called with the dropped or selected files. */
	onDropFiles: (files: FileList) => void;
	isDisabled?: boolean;
	accept?: string;
	multiple?: boolean;
	/** Primary line inside the drop zone (not the HTML `title` tooltip). */
	title?: ReactNode;
	/** Secondary line (e.g. allowed types). */
	hint?: ReactNode;
}

const DropZone = ({
	onDropFiles,
	isDisabled = false,
	accept,
	multiple = true,
	title = "Click to upload or drag and drop",
	hint,
	className,
	...props
}: DropZoneProps) => {
	const inputId = useId();
	const inputRef = useRef<HTMLInputElement>(null);
	const [isDragging, setIsDragging] = useState(false);

	const emitFiles = useCallback(
		(fileList: FileList | null) => {
			if (!fileList || fileList.length === 0 || isDisabled) return;
			onDropFiles(fileList);
			if (inputRef.current) inputRef.current.value = "";
		},
		[isDisabled, onDropFiles],
	);

	const onDragOver = useCallback(
		(e: DragEvent) => {
			if (isDisabled) return;
			e.preventDefault();
			e.stopPropagation();
			setIsDragging(true);
		},
		[isDisabled],
	);

	const onDragLeave = useCallback((e: DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
	}, []);

	const onDrop = useCallback(
		(e: DragEvent) => {
			if (isDisabled) return;
			e.preventDefault();
			e.stopPropagation();
			setIsDragging(false);
			emitFiles(e.dataTransfer.files);
		},
		[isDisabled, emitFiles],
	);

	const openPicker = useCallback(() => {
		if (isDisabled) return;
		inputRef.current?.click();
	}, [isDisabled]);

	return (
		<div className={cx("w-full", className)} {...props}>
			<input
				ref={inputRef}
				id={inputId}
				type="file"
				accept={accept}
				multiple={multiple}
				className="sr-only"
				disabled={isDisabled}
				onChange={(e) => emitFiles(e.target.files)}
			/>
			<button
				type="button"
				disabled={isDisabled}
				aria-labelledby={`${inputId}-label`}
				onClick={openPicker}
				onDragEnter={onDragOver}
				onDragOver={onDragOver}
				onDragLeave={onDragLeave}
				onDrop={onDrop}
				className={cx(
					"flex w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-xl px-6 py-10 outline-hidden ring-1 ring-inset transition-colors",
					"bg-primary ring-primary",
					!isDisabled &&
						"hover:bg-primary_hover focus-visible:ring-2 focus-visible:ring-focus-ring",
					isDragging && !isDisabled && "bg-brand-primary_alt ring-brand",
					isDisabled && "cursor-not-allowed opacity-50",
				)}
			>
				<span
					className={cx(
						"flex size-12 items-center justify-center rounded-lg shadow-xs ring-1 ring-inset",
						"bg-primary text-fg-quaternary ring-primary",
					)}
					aria-hidden
				>
					<UploadCloud02 className="size-6" />
				</span>
				<div className="flex max-w-md flex-col gap-1 text-center">
					<p
						id={`${inputId}-label`}
						className="text-sm font-medium text-secondary"
					>
						{title}
					</p>
					{hint != null && hint !== "" && (
						<p className="text-sm text-tertiary">{hint}</p>
					)}
				</div>
			</button>
		</div>
	);
};

interface ButtonPickerProps
	extends Omit<ComponentPropsWithRef<"div">, "children" | "title"> {
	/** Called with the chosen files (picker only; no drag-and-drop). */
	onDropFiles: (files: FileList) => void;
	isDisabled?: boolean;
	accept?: string;
	multiple?: boolean;
	/** Label shown on the button (not the HTML `title` tooltip). */
	title?: ReactNode;
	hint?: ReactNode;
}

/**
 * Opens the native file picker via a compact button (no drag-and-drop target).
 * Pairs with {@link FileUpload.List} / {@link FileUpload.ListItemProgressBar} like `FileUpload.DropZone`.
 */
const ButtonPicker = ({
	onDropFiles,
	isDisabled = false,
	accept,
	multiple = true,
	title,
	hint,
	className,
	...props
}: ButtonPickerProps) => {
	const inputId = useId();
	const hintId = useId();
	const inputRef = useRef<HTMLInputElement>(null);

	const emitFiles = useCallback(
		(fileList: FileList | null) => {
			if (!fileList || fileList.length === 0 || isDisabled) return;
			onDropFiles(fileList);
			if (inputRef.current) inputRef.current.value = "";
		},
		[isDisabled, onDropFiles],
	);

	const openPicker = useCallback(() => {
		if (isDisabled) return;
		inputRef.current?.click();
	}, [isDisabled]);

	const defaultTitle = multiple ? "Choose files" : "Choose file";
	const label = title ?? defaultTitle;
	const hasHint = hint != null && hint !== "";

	return (
		<div className={cx("flex w-full flex-col gap-1.5", className)} {...props}>
			<input
				ref={inputRef}
				id={inputId}
				type="file"
				accept={accept}
				multiple={multiple}
				className="sr-only"
				disabled={isDisabled}
				onChange={(e) => emitFiles(e.target.files)}
			/>
			<div className="flex flex-wrap items-center gap-2">
				<Button
					type="button"
					size="sm"
					color="secondary"
					iconLeading={UploadCloud02}
					isDisabled={isDisabled}
					onClick={openPicker}
					aria-describedby={hasHint ? hintId : undefined}
				>
					{label}
				</Button>
				{hasHint ? (
					<span id={hintId} className="text-xs text-tertiary">
						{hint}
					</span>
				) : null}
			</div>
		</div>
	);
};

const List = ({ className, ...props }: ComponentPropsWithRef<"ul">) => (
	<ul
		{...props}
		className={cx("flex list-none flex-col gap-3 p-0", className)}
	/>
);

interface ListItemProgressBarProps extends UploadedFile {
	onDelete: () => void;
	onRetry?: () => void;
	className?: string;
}

const ListItemProgressBar = ({
	id: _id,
	name,
	size,
	type,
	progress,
	failed = false,
	onDelete,
	onRetry,
	className,
}: ListItemProgressBarProps) => {
	const clamped = Math.min(100, Math.max(0, progress));

	return (
		<li
			className={cx(
				"flex gap-3 rounded-xl bg-primary p-4 ring-1 ring-inset ring-secondary",
				className,
			)}
		>
			<div className="hidden shrink-0 sm:block" aria-hidden>
				<FileIcon type={type} theme="light" className="size-10 dark:hidden" />
				<FileIcon
					type={type}
					theme="dark"
					className="size-10 not-dark:hidden"
				/>
			</div>
			<div className="min-w-0 flex-1">
				<div className="flex items-start justify-between gap-2">
					<div className="min-w-0">
						<p className="truncate text-sm font-medium text-secondary">
							{name}
						</p>
						<p className="text-sm text-tertiary">{getReadableFileSize(size)}</p>
					</div>
					<div className="flex shrink-0 gap-1">
						{failed && onRetry && (
							<ButtonUtility
								type="button"
								icon={RefreshCw01}
								tooltip={`Retry upload for ${name}`}
								color="tertiary"
								onClick={onRetry}
							/>
						)}
						<ButtonUtility
							type="button"
							icon={Trash02}
							tooltip={`Remove ${name}`}
							color="tertiary"
							onClick={onDelete}
						/>
					</div>
				</div>
				<div className="mt-3">
					{failed ? (
						<>
							<p className="mb-2 text-sm text-fg-error-primary">
								Upload failed
							</p>
							<ProgressBarBase
								value={0}
								progressClassName="bg-fg-error-primary"
							/>
						</>
					) : (
						<ProgressBarBase
							value={clamped}
							progressClassName={
								clamped >= 100 ? "bg-fg-success-primary" : undefined
							}
						/>
					)}
				</div>
			</div>
		</li>
	);
};

const FileUpload = Root as typeof Root & {
	Root: typeof Root;
	DropZone: typeof DropZone;
	ButtonPicker: typeof ButtonPicker;
	List: typeof List;
	ListItemProgressBar: typeof ListItemProgressBar;
};

FileUpload.Root = Root;
FileUpload.DropZone = DropZone;
FileUpload.ButtonPicker = ButtonPicker;
FileUpload.List = List;
FileUpload.ListItemProgressBar = ListItemProgressBar;

export { FileUpload };
export type { ButtonPickerProps };
